import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  X,
  Type,
  Hash,
  ToggleLeft,
  Users,
  List,
  LayoutGrid,
  MousePointerClick,
  UsersRound,
  Boxes,
} from 'lucide-react';
import Icon from 'shared/components/Icon';
import axiosInstance from 'shared/services/api/axiosInstance';
import {
  getFixedUserProperties,
  getDefaultOperator,
} from 'features/activity/config/userFilterProperties';
import { DEFAULT_WHO_TIME_FILTER } from 'features/activity/utils/whoTimeFilterUtils';
import { activityFilterPopupTheme as analyticsTheme } from 'shared/theme/activityFilterPopupTheme';
import {
  getPropertyDescriptionPreview,
  getCohortFilterDescription,
} from 'features/activity/components/propertyFilterDescriptions';
import { primary as PRIMARY } from "shared/theme/trodoColors";

const ObjectIcon = ({ color = 'currentColor', size = 18 }) => (
  <span style={{ fontSize: size, color }} className="font-mono">{'{}'}</span>
);

function getDataTypeIcon(type) {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'datetime':
    case 'date': return ({ color: c = 'currentColor', size: s = 18 }) => <Icon name="calendar" size={s} color={c} />;
    case 'boolean': return ToggleLeft;
    case 'list': return List;
    case 'object': return ObjectIcon;
    default: return Type;
  }
}

function propsMatchForPreview(a, b) {
  if (!a || !b) return false;
  return (
    a.source === b.source &&
    a.name === b.name &&
    (a.groupKey || '') === (b.groupKey || '')
  );
}

function filterMatchesExisting(prop, existingFilter) {
  if (!existingFilter) return false;
  const fld = existingFilter.field || existingFilter.property;
  const src = existingFilter.propertySource || existingFilter.category;
  if (fld !== prop.name || src !== prop.source) return false;
  if (prop.source === 'group') {
    return (existingFilter.groupKey || '') === (prop.groupKey || '');
  }
  return true;
}

/**
 * User filter property picker: All / Users / Events / Groups (cohorts builder) / Cohorts (elsewhere).
 * UI aligned with Insights EventPropertyFilterModule (three-pane + analytics theme).
 */
const UserPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  teamId,
  filters = [],
  existingFilter = null,
  onAdd = () => {},
  cohortBuilder = false,
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [userProperties, setUserProperties] = useState(() => getFixedUserProperties());
  const [eventProperties, setEventProperties] = useState([]);
  const [loadingUserCustom, setLoadingUserCustom] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [groupProperties, setGroupProperties] = useState([]);
  const [loadingGroupProps, setLoadingGroupProps] = useState(false);
  const [expandedObjectProperty, setExpandedObjectProperty] = useState(null);
  const [previewProp, setPreviewProp] = useState(null);
  const popupRef = useRef(null);

  const getNumericTeamId = () => {
    let id = typeof teamId === 'object' && teamId !== null
      ? parseInt(teamId._id || teamId.id, 10)
      : parseInt(teamId, 10);
    if (typeof teamId === 'string' && isNaN(id)) {
      try {
        const td = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        id = parseInt(td._id || td.id, 10);
      } catch (_) {}
    }
    return isNaN(id) ? null : id;
  };

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const popupWidth = 820;
    const popupHeight = 440;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16;

    let top;
    let left;

    if (triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      top = triggerRect.bottom + scrollY + 8;
      left = triggerRect.left + scrollX;
      if (left + popupWidth > viewportWidth - margin) {
        left = triggerRect.right + scrollX - popupWidth;
        if (left < margin) left = viewportWidth - popupWidth - margin;
      }
      if (left < margin) left = margin;
      if (top + popupHeight > viewportHeight + scrollY - margin) {
        top = triggerRect.top + scrollY - popupHeight - 8;
        if (top < scrollY + margin) top = scrollY + margin;
      }
    } else {
      left = scrollX + (viewportWidth - popupWidth) / 2;
      top = scrollY + (viewportHeight - popupHeight) / 2;
    }
    if (top < scrollY + margin) top = scrollY + margin;
    setPosition({ top, left });
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        if (expandedObjectProperty) setExpandedObjectProperty(null);
        else onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, expandedObjectProperty]);

  useEffect(() => { if (!isOpen) setExpandedObjectProperty(null); }, [isOpen]);

  useEffect(() => {
    if (!cohortBuilder || !isOpen) return;
    if (selectedNavItem === 'cohorts') {
      setSelectedNavItem('all');
    }
  }, [cohortBuilder, isOpen, selectedNavItem]);

  useEffect(() => {
    if (!cohortBuilder || !isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingGroupProps(true);
    axiosInstance
      .get('/group-identities/cohort-filter-properties', { params: { team_id: numericTeamId } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.properties)) {
          setGroupProperties(res.data.properties);
        } else {
          setGroupProperties([]);
        }
      })
      .catch(() => setGroupProperties([]))
      .finally(() => setLoadingGroupProps(false));
  }, [cohortBuilder, isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingUserCustom(true);
    axiosInstance.get('/events/user-custom-properties', { params: { team_id: numericTeamId, limit: 5000 } })
      .then((res) => {
        if (res.data?.success && res.data.custom_properties) {
          const custom = res.data.custom_properties.map((prop) => {
            let propertyType = prop.type || 'string';
            let listItemType = null;
            if (propertyType.startsWith('list<') && propertyType.endsWith('>')) {
              listItemType = propertyType.slice(5, -1);
              propertyType = 'list';
            } else if (propertyType === 'list') {
              listItemType = 'string';
            }
            return {
              name: `custom.${prop.name}`,
              label: prop.name,
              type: propertyType,
              listItemType,
              source: 'user',
              isCustom: true,
              sampleValues: prop.sampleValues || [],
              nestedProperties: prop.nestedProperties || undefined,
            };
          });
          setUserProperties([...getFixedUserProperties(), ...custom]);
        }
      })
      .catch(() => setUserProperties(getFixedUserProperties()))
      .finally(() => setLoadingUserCustom(false));
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!isOpen || !teamId) return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingEvents(true);
    axiosInstance.get('/events/summaries', { params: { team_id: numericTeamId } })
      .then((res) => {
        const summaries = res.data?.summaries || [];
        const names = [...new Set(summaries.map((s) => s.event_name).filter(Boolean))].sort();
        setEventProperties(names.map((n) => ({
          name: `event_count:${n}`,
          label: n,
          type: 'event_count',
          source: 'event',
        })));
      })
      .catch(() => setEventProperties([]))
      .finally(() => setLoadingEvents(false));
  }, [isOpen, teamId]);

  useEffect(() => {
    if (cohortBuilder || !isOpen || selectedNavItem !== 'cohorts') return;
    const numericTeamId = getNumericTeamId();
    if (!numericTeamId) return;
    setLoadingCohorts(true);
    axiosInstance.get('/cohorts', { params: { team_id: numericTeamId, include_user_count: false } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.cohorts)) {
          setCohorts(res.data.cohorts);
        }
      })
      .catch(() => setCohorts([]))
      .finally(() => setLoadingCohorts(false));
  }, [cohortBuilder, isOpen, selectedNavItem, teamId]);

  const allProperties = useMemo(
    () =>
      cohortBuilder
        ? [...userProperties, ...eventProperties, ...groupProperties]
        : [...userProperties, ...eventProperties],
    [cohortBuilder, userProperties, eventProperties, groupProperties]
  );

  const propsList = useMemo(() => {
    switch (selectedNavItem) {
      case 'users': return userProperties;
      case 'events': return eventProperties;
      case 'groups': return groupProperties;
      case 'all': return allProperties;
      default: return allProperties;
    }
  }, [selectedNavItem, userProperties, eventProperties, groupProperties, allProperties]);

  const filteredProps = useMemo(
    () =>
      propsList.filter(
        (p) =>
          (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [propsList, searchTerm]
  );

  const filteredCohorts = useMemo(
    () =>
      cohorts.filter(
        (c) => !searchTerm || (c.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [cohorts, searchTerm]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (expandedObjectProperty) return;
    if (!['all', 'users', 'events', 'groups'].includes(selectedNavItem)) return;
    if (filteredProps.length === 0) {
      setPreviewProp(null);
      return;
    }
    setPreviewProp((prev) => {
      if (prev && !prev.kind && filteredProps.some((p) => propsMatchForPreview(prev, p))) {
        return prev;
      }
      return filteredProps[0];
    });
  }, [isOpen, selectedNavItem, filteredProps, expandedObjectProperty]);

  useEffect(() => {
    if (!isOpen || cohortBuilder || selectedNavItem !== 'cohorts') return;
    if (filteredCohorts.length === 0) {
      setPreviewProp(null);
      return;
    }
    setPreviewProp((prev) => {
      if (prev?.kind === 'cohort' && filteredCohorts.some((c) => c.id === prev.cohort.id)) {
        return prev;
      }
      return { kind: 'cohort', cohort: filteredCohorts[0] };
    });
  }, [isOpen, cohortBuilder, selectedNavItem, filteredCohorts]);

  const getCategory = (prop) => {
    if (prop.source === 'user') return 'user';
    if (prop.source === 'group') return 'group';
    if (prop.source === 'event' || prop.type === 'event_count') return 'event';
    return 'user';
  };

  const handleCohortClick = (cohort) => {
    onAdd({
      id: existingFilter?.id || `user-filter-cohort-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category: 'cohort',
      propertySource: 'cohort',
      field: 'cohort',
      value: [cohort.id],
      propertyLabel: cohort.name,
      connector: existingFilter?.connector ?? 'AND',
    });
    onClose();
  };

  const handleSelectNestedProperty = (parentProperty, nestedProp) => {
    const category = getCategory(parentProperty);
    const nestedType = nestedProp.type || 'string';
    let listItemType = null;
    if (nestedType.startsWith('list<') && nestedType.endsWith('>')) {
      listItemType = nestedType.slice(5, -1);
    }
    const fullPath = `${parentProperty.name}.${nestedProp.name}`;
    const effectiveType = nestedType.startsWith('list') ? 'list' : nestedType;
    const newFilter = {
      id: existingFilter?.id || `user-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category,
      propertySource: category,
      connector: existingFilter?.connector,
      field: fullPath,
      property: fullPath,
      propertyLabel: `${parentProperty.label}.${nestedProp.name}`,
      propertyType: effectiveType,
      listItemType: listItemType || undefined,
      operator: getDefaultOperator(effectiveType),
      value: null,
      value2: null,
      sampleValues: nestedProp.sampleValues || [],
      nestedPropertyKey: nestedProp.name,
      listMode: effectiveType === 'list' ? 'any_in_list' : undefined,
    };
    onAdd(newFilter);
    setExpandedObjectProperty(null);
    onClose();
  };

  const handleSelectProperty = (property) => {
    if (property.type === 'object' && property.nestedProperties?.length > 0) {
      setExpandedObjectProperty(property);
      return;
    }
    const category = getCategory(property);
    const isCountType = property.type === 'event_count';
    const effectiveType = isCountType ? 'number' : property.type;
    const operator = isCountType ? 'equal' : getDefaultOperator(property.type);

    const newFilter = {
      id: existingFilter?.id || `user-filter-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      category,
      propertySource: category,
      connector: existingFilter?.connector,
      field: property.name,
      property: property.name,
      propertyLabel: property.label,
      propertyType: effectiveType,
      ...(property.groupKey != null && property.groupKey !== '' && { groupKey: property.groupKey }),
      operator: existingFilter && existingFilter.propertyType === effectiveType ? existingFilter.operator : operator,
      value: existingFilter && existingFilter.propertyType === effectiveType ? existingFilter.value : null,
      value2: existingFilter?.value2 ?? null,
      ...(property.sampleValues && { sampleValues: property.sampleValues }),
      ...(property.listItemType != null && { listItemType: property.listItemType }),
      ...(property.nestedProperties && { nestedProperties: property.nestedProperties }),
      ...(property.type === 'list' && { listMode: existingFilter?.listMode || 'any_in_list' }),
      ...(isCountType && {
        didOperator: existingFilter?.didOperator ?? 'did',
        eventTimeFilter: existingFilter?.eventTimeFilter ?? { ...DEFAULT_WHO_TIME_FILTER },
      }),
    };
    onAdd(newFilter);
    onClose();
  };

  const navItems = useMemo(() => {
    const base = [
      { id: 'all', label: 'All', NavIcon: LayoutGrid },
      { id: 'users', label: 'Users', NavIcon: Users },
      { id: 'events', label: 'Events', NavIcon: MousePointerClick },
    ];
    if (cohortBuilder) {
      base.push({ id: 'groups', label: 'Groups', NavIcon: Boxes });
    } else {
      base.push({ id: 'cohorts', label: 'Cohorts', NavIcon: UsersRound });
    }
    return base;
  }, [cohortBuilder]);

  const isLoading = cohortBuilder
    ? (
        (loadingUserCustom && (selectedNavItem === 'all' || selectedNavItem === 'users')) ||
        (loadingEvents && (selectedNavItem === 'events' || selectedNavItem === 'all')) ||
        (loadingGroupProps && (selectedNavItem === 'groups' || selectedNavItem === 'all'))
      )
    : (
        (loadingUserCustom && selectedNavItem !== 'events' && selectedNavItem !== 'cohorts') ||
        (loadingEvents && (selectedNavItem === 'events' || selectedNavItem === 'all')) ||
        (loadingCohorts && selectedNavItem === 'cohorts')
      );

  const renderDescriptionPane = () => {
    if (expandedObjectProperty) {
      const desc = getPropertyDescriptionPreview(expandedObjectProperty);
      return (
        <>
          <h3 className="text-sm font-semibold leading-tight mb-2" style={{ color: analyticsTheme.textPrimary }}>
            {expandedObjectProperty.label || expandedObjectProperty.name}
          </h3>
          <p className="text-xs leading-relaxed" style={{ color: analyticsTheme.textMuted }}>
            {desc?.body || 'Pick a nested field from the list. Each path becomes a separate filter on your user profile.'}
          </p>
        </>
      );
    }
    if (!cohortBuilder && selectedNavItem === 'cohorts') {
      const c = previewProp?.kind === 'cohort' ? previewProp.cohort : null;
      return (
        <>
          <div className="flex items-start gap-2 mb-2">
            <UsersRound className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: PRIMARY }} />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
                {c?.name || 'Cohorts'}
              </h3>
            </div>
          </div>
          <p className="text-xs leading-relaxed" style={{ color: analyticsTheme.textWarmMuted }}>
            {c ? getCohortFilterDescription() : 'Select a cohort to see how membership filtering works in this list.'}
          </p>
        </>
      );
    }

    const prop = previewProp && !previewProp.kind ? previewProp : null;
    if (!prop) {
      return (
        <p className="text-xs" style={{ color: analyticsTheme.textMuted }}>
          Hover a property to see its description.
        </p>
      );
    }

    const desc = getPropertyDescriptionPreview(prop);
    if (!desc) {
      return null;
    }
    if (desc.titleOnly) {
      return (
        <h3 className="text-sm font-semibold leading-tight" style={{ color: analyticsTheme.textPrimary }}>
          {desc.title}
        </h3>
      );
    }
    return (
      <>
        <h3 className="text-sm font-semibold leading-tight mb-2" style={{ color: analyticsTheme.textPrimary }}>
          {desc.title}
        </h3>
        <p className="text-xs leading-relaxed" style={{ color: analyticsTheme.textWarmMuted }}>
          {desc.body}
        </p>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[9998] bg-[color:var(--surface-overlay)]"
        onClick={onClose}
        aria-hidden
      />
      {createPortal(
        <div
          ref={popupRef}
          className="fixed z-[9999] rounded-xl border shadow-2xl flex flex-col overflow-hidden text-left"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            minWidth: 'min(760px, 96vw)',
            maxWidth: 'min(920px, 96vw)',
            maxHeight: '65vh',
            fontFamily: 'Inter, sans-serif',
            backgroundColor: analyticsTheme.bgRaised,
            borderColor: analyticsTheme.border,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden" style={{ minHeight: 280 }}>
            <nav
              className="w-full sm:w-[148px] flex-shrink-0 border-b sm:border-b-0 sm:border-r flex flex-row sm:flex-col overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto"
              style={{ borderColor: analyticsTheme.border, backgroundColor: analyticsTheme.bgNav }}
            >
              <div
                className="hidden sm:block border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]"
                style={{ borderColor: analyticsTheme.border }}
              >
                Categories
              </div>
              <div className="flex flex-1 sm:flex-col flex-row overflow-x-auto sm:overflow-x-visible sm:overflow-y-auto py-1">
                {navItems.map((item) => {
                  const active = selectedNavItem === item.id;
                  const NavIcon = item.NavIcon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedNavItem(item.id)}
                      className="flex flex-shrink-0 sm:w-full w-auto items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors whitespace-nowrap"
                      style={{
                        backgroundColor: active ? analyticsTheme.navActiveBg : 'transparent',
                        color: active ? PRIMARY : 'var(--text-secondary)',
                        fontWeight: active ? 600 : 500,
                      }}
                    >
                      <NavIcon size={14} style={{ color: active ? PRIMARY : 'var(--text-disabled)' }} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </nav>

            <div
              className="flex-1 min-w-0 flex flex-col border-r"
              style={{ borderColor: analyticsTheme.border }}
            >
              {/* Inline search */}
              <div className="flex items-center gap-2 border-b px-3 py-2 flex-shrink-0" style={{ borderColor: analyticsTheme.border }}>
                <Search size={14} className="text-[color:var(--text-muted)]" />
                <input
                  autoFocus
                  type="search"
                  placeholder={
                    !cohortBuilder && selectedNavItem === 'cohorts'
                      ? 'Search cohorts…'
                      : 'Search properties…'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
                />
                {searchTerm ? (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                    aria-label="Close"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {expandedObjectProperty ? (
                  <div className="px-2 pb-2">
                    <button
                      type="button"
                      onClick={() => setExpandedObjectProperty(null)}
                      className="mb-2 text-xs font-medium transition-colors"
                      style={{ color: PRIMARY }}
                    >
                      ← Back to properties
                    </button>
                    <div className="text-xs font-medium mb-2" style={{ color: analyticsTheme.textMuted }}>
                      Nested fields in &quot;{expandedObjectProperty.label}&quot;
                    </div>
                    <div className="space-y-0">
                      {expandedObjectProperty.nestedProperties?.map((np) => {
                        const NestedIcon = getDataTypeIcon(np.type?.startsWith('list') ? 'list' : np.type || 'string');
                        return (
                          <button
                            key={np.name}
                            type="button"
                            onClick={() => handleSelectNestedProperty(expandedObjectProperty, np)}
                            className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors rounded-lg"
                            style={{ color: analyticsTheme.textPrimary }}
                          >
                            <NestedIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--text-disabled)' }} />
                            <span className="flex-1 font-medium truncate">{np.name}</span>
                            <span className="text-xs flex-shrink-0" style={{ color: analyticsTheme.textMuted }}>
                              {np.type || 'string'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : !cohortBuilder && selectedNavItem === 'cohorts' ? (
                  <>
                    {loadingCohorts ? (
                      <div className="px-3 py-6 text-sm text-center" style={{ color: analyticsTheme.textMuted }}>
                        Loading cohorts…
                      </div>
                    ) : filteredCohorts.length === 0 ? (
                      <div className="px-3 py-6 text-sm text-center" style={{ color: analyticsTheme.textMuted }}>
                        {searchTerm ? 'No cohorts match your search.' : 'No cohorts. Create cohorts on the Cohorts page.'}
                      </div>
                    ) : (
                      filteredCohorts.map((cohort) => {
                        const isPrev = previewProp?.kind === 'cohort' && previewProp.cohort?.id === cohort.id;
                        return (
                          <button
                            key={cohort.id}
                            type="button"
                            onClick={() => handleCohortClick(cohort)}
                            onMouseEnter={() => setPreviewProp({ kind: 'cohort', cohort })}
                            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                            style={{ backgroundColor: isPrev ? analyticsTheme.rowHoverBg : 'transparent' }}
                          >
                            <span className="font-mono text-[color:var(--text-primary)] truncate">{cohort.name}</span>
                            <span className="ml-2 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">cohort</span>
                          </button>
                        );
                      })
                    )}
                  </>
                ) : isLoading ? (
                  <div className="px-3 py-6 text-sm text-center" style={{ color: analyticsTheme.textMuted }}>
                    Loading…
                  </div>
                ) : filteredProps.length === 0 ? (
                  <div className="px-3 py-6 text-sm text-center" style={{ color: analyticsTheme.textMuted }}>
                    {searchTerm ? 'No properties found matching your search.' : 'No properties available.'}
                  </div>
                ) : (
                  <div className="px-1 pb-2">
                    {filteredProps.map((prop, idx) => {
                      const isSelected = filterMatchesExisting(prop, existingFilter);
                      const isPrev =
                        previewProp &&
                        !previewProp.kind &&
                        propsMatchForPreview(previewProp, prop);
                      const tag = prop.source === 'user'
                        ? 'user'
                        : prop.source === 'group'
                          ? (prop.groupKey || 'group')
                          : (prop.type || 'event');
                      const rowKey = `${prop.source}-${prop.name}-${prop.groupKey || ''}-${idx}`;
                      return (
                        <button
                          key={rowKey}
                          type="button"
                          onClick={() => handleSelectProperty(prop)}
                          onMouseEnter={() => setPreviewProp(prop)}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                          style={{ backgroundColor: isPrev ? analyticsTheme.rowHoverBg : 'transparent' }}
                        >
                          <span className="font-mono text-[color:var(--text-primary)] truncate">{prop.label}</span>
                          <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">
                            {isSelected && <span style={{ color: PRIMARY }}>current</span>}
                            {tag}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div
              className="hidden sm:flex w-[260px] max-w-[38vw] flex-shrink-0 flex-col p-3 overflow-y-auto"
              style={{ backgroundColor: analyticsTheme.bgDescription }}
            >
              {renderDescriptionPane()}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserPropertyFilterModule;
export { getDataTypeIcon };
